import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import './CustomerFeedback.css';

const API = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

const TYPE_CONFIG = {
  complaint:   { label: 'Complaint',   cls: 'cf-type-complaint',   icon: '⚠' },
  compliment:  { label: 'Compliment',  cls: 'cf-type-compliment',  icon: '★' },
  suggestion:  { label: 'Suggestion',  cls: 'cf-type-suggestion',  icon: '💡' },
  enquiry:     { label: 'Enquiry',     cls: 'cf-type-enquiry',     icon: '?' },
};

const STATUS_CONFIG = {
  open:          { label: 'Open',        cls: 'cf-status-open'       },
  'in progress': { label: 'In Progress', cls: 'cf-status-inprogress' },
  closed:        { label: 'Closed',      cls: 'cf-status-closed'     },
};

const EMPTY_FORM = {
  type: 'complaint', customer_name: '', received_date: new Date().toISOString().slice(0,10),
  description: '', action_taken: '', status: 'open',
  satisfaction_rating: '', closed_date: '', notes: '',
};

function StarRating({ rating, onChange }) {
  return (
    <div className="cf-stars">
      {[1,2,3,4,5].map(n => (
        <span key={n} className={`cf-star ${rating >= n ? 'filled' : ''}`} onClick={() => onChange && onChange(n)}>★</span>
      ))}
    </div>
  );
}

function formatDate(str) {
  if (!str) return '—';
  return new Date(str).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

export default function CustomerFeedback({ user }) {
  const [feedback, setFeedback] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('list');
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [typeFilter, setTypeFilter] = useState('All');
  const [statusFilter, setStatusFilter] = useState('All');

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    try {
      const [fbRes, userRes] = await Promise.all([
        axios.get(`${API}/customer-feedback`),
        axios.get(`${API}/users/all`),
      ]);
      setFeedback(fbRes.data);
      setUsers(userRes.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const counts = useMemo(() => ({
    total:      feedback.length,
    complaints: feedback.filter(f => f.type === 'complaint').length,
    open:       feedback.filter(f => f.status === 'open').length,
    closed:     feedback.filter(f => f.status === 'closed').length,
  }), [feedback]);

  const filtered = useMemo(() => {
    return feedback
      .filter(f => typeFilter === 'All' || f.type === typeFilter)
      .filter(f => statusFilter === 'All' || f.status === statusFilter);
  }, [feedback, typeFilter, statusFilter]);

  const handleSave = async () => {
    if (!form.description) { alert('Description is required.'); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        satisfaction_rating: form.satisfaction_rating ? parseInt(form.satisfaction_rating) : null,
        closed_date: form.status === 'closed' && !form.closed_date ? new Date().toISOString().slice(0,10) : form.closed_date || null,
        closed_by: form.status === 'closed' ? user.id : null,
        created_by: user.id,
      };
      if (selected && view === 'edit') {
        await axios.patch(`${API}/customer-feedback/${selected.id}`, payload);
      } else {
        await axios.post(`${API}/customer-feedback`, payload);
      }
      setView('list');
      setForm(EMPTY_FORM);
      setSelected(null);
      fetchAll();
    } catch (err) { alert('Error saving: ' + err.message); }
    finally { setSaving(false); }
  };

  const handleClose = async (fb) => {
    try {
      await axios.patch(`${API}/customer-feedback/${fb.id}`, {
        status: 'closed',
        closed_date: new Date().toISOString().slice(0,10),
        closed_by: user.id,
      });
      fetchAll();
      if (selected?.id === fb.id) setSelected({ ...selected, status: 'closed' });
    } catch (err) { alert('Error closing feedback.'); }
  };

  const handleDelete = async (fb) => {
    if (!window.confirm('Permanently delete this feedback record?')) return;
    try {
      await axios.delete(`${API}/customer-feedback/${fb.id}`);
      setView('list');
      setSelected(null);
      fetchAll();
    } catch (err) { alert('Error deleting.'); }
  };

  const openEdit = (fb) => {
    setSelected(fb);
    setForm({
      type: fb.type || 'complaint',
      customer_name: fb.customer_name || '',
      received_date: fb.received_date || '',
      description: fb.description || '',
      action_taken: fb.action_taken || '',
      status: fb.status || 'open',
      satisfaction_rating: fb.satisfaction_rating || '',
      closed_date: fb.closed_date || '',
      notes: fb.notes || '',
    });
    setView('edit');
  };

  if (loading) return <div className="cf-loading">Loading customer feedback...</div>;

  // ── FORM ─────────────────────────────────────────────────────
  if (view === 'add' || view === 'edit') return (
    <div className="cf-form-page">
      <button className="cf-back" onClick={() => { setView('list'); setForm(EMPTY_FORM); setSelected(null); }}>
        ← CUSTOMER FEEDBACK
      </button>
      <div className="cf-form-header">
        <h2>{view === 'edit' ? 'EDIT FEEDBACK' : 'LOG FEEDBACK'}</h2>
        {view === 'edit' && selected && <p className="cf-ref">{selected.reference_number}</p>}
      </div>
      <div className="cf-form">
        <div className="cf-field">
          <label>TYPE</label>
          <select value={form.type} onChange={e => setForm({ ...form, type: e.target.value })}>
            <option value="complaint">Complaint</option>
            <option value="compliment">Compliment</option>
            <option value="suggestion">Suggestion</option>
            <option value="enquiry">Enquiry</option>
          </select>
        </div>
        <div className="cf-field">
          <label>STATUS</label>
          <select value={form.status} onChange={e => setForm({ ...form, status: e.target.value })}>
            <option value="open">Open</option>
            <option value="in progress">In Progress</option>
            <option value="closed">Closed</option>
          </select>
        </div>
        <div className="cf-field">
          <label>CUSTOMER NAME</label>
          <input value={form.customer_name} onChange={e => setForm({ ...form, customer_name: e.target.value })} placeholder="Customer or company name" />
        </div>
        <div className="cf-field">
          <label>DATE RECEIVED</label>
          <input type="date" value={form.received_date} onChange={e => setForm({ ...form, received_date: e.target.value })} />
        </div>
        <div className="cf-field">
          <label>SATISFACTION RATING</label>
          <StarRating rating={parseInt(form.satisfaction_rating) || 0} onChange={n => setForm({ ...form, satisfaction_rating: n })} />
        </div>
        <div className="cf-field full">
          <label>DESCRIPTION *</label>
          <textarea rows={4} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="What was the feedback?" />
        </div>
        <div className="cf-field full">
          <label>ACTION TAKEN</label>
          <textarea rows={3} value={form.action_taken} onChange={e => setForm({ ...form, action_taken: e.target.value })} placeholder="What action was taken in response?" />
        </div>
        <div className="cf-field full">
          <label>NOTES</label>
          <textarea rows={2} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} placeholder="Any additional notes..." />
        </div>
        <div className="cf-form-actions">
          <button className="cf-btn secondary" onClick={() => { setView('list'); setForm(EMPTY_FORM); setSelected(null); }}>CANCEL</button>
          <button className={`cf-btn primary ${saving ? 'disabled' : ''}`} onClick={handleSave} disabled={saving}>
            {saving ? 'SAVING...' : view === 'edit' ? 'SAVE CHANGES' : 'LOG FEEDBACK'}
          </button>
        </div>
      </div>
    </div>
  );

  // ── DETAIL ───────────────────────────────────────────────────
  if (view === 'detail' && selected) {
    const type = TYPE_CONFIG[selected.type] || TYPE_CONFIG.complaint;
    const status = STATUS_CONFIG[selected.status] || STATUS_CONFIG.open;
    return (
      <div className="cf-detail-page">
        <button className="cf-back" onClick={() => { setView('list'); setSelected(null); }}>← CUSTOMER FEEDBACK</button>
        <div className="cf-detail-header">
          <div className="cf-detail-ref">{selected.reference_number}</div>
          <div className="cf-detail-meta">
            <span className={`cf-type-badge ${type.cls}`}>{type.icon} {type.label}</span>
            <span className={`cf-status-badge ${status.cls}`}>{status.label}</span>
            {selected.satisfaction_rating && <StarRating rating={selected.satisfaction_rating} />}
          </div>
          {selected.customer_name && <div className="cf-detail-customer">{selected.customer_name}</div>}
          <div className="cf-detail-date">Received: {formatDate(selected.received_date)}</div>
        </div>

        <div className="cf-detail-grid">
          <div className="cf-detail-block">
            <div className="cf-block-title">FEEDBACK</div>
            <p className="cf-block-text">{selected.description}</p>
          </div>
          {selected.action_taken && (
            <div className="cf-detail-block">
              <div className="cf-block-title">ACTION TAKEN</div>
              <p className="cf-block-text">{selected.action_taken}</p>
            </div>
          )}
          {selected.notes && (
            <div className="cf-detail-block">
              <div className="cf-block-title">NOTES</div>
              <p className="cf-block-text">{selected.notes}</p>
            </div>
          )}
          {selected.status === 'closed' && (
            <div className="cf-detail-block closed-block">
              <div className="cf-block-title">CLOSED</div>
              <div className="cf-block-row"><span>Closed on</span><span>{formatDate(selected.closed_date)}</span></div>
            </div>
          )}
        </div>

        {user.role === 'admin' && (
          <div className="cf-detail-actions">
            {selected.status !== 'closed' && <button className="cf-btn primary" onClick={() => handleClose(selected)}>✓ CLOSE</button>}
            <button className="cf-btn secondary" onClick={() => openEdit(selected)}>EDIT</button>
            <button className="cf-btn danger" onClick={() => handleDelete(selected)}>DELETE</button>
          </div>
        )}
      </div>
    );
  }

  // ── LIST ─────────────────────────────────────────────────────
  return (
    <div className="cf-page">
      <div className="cf-stats">
        <div className="cf-stat"><span className="cf-stat-value">{counts.total}</span><span className="cf-stat-label">Total</span></div>
        <div className="cf-stat complaint"><span className="cf-stat-value">{counts.complaints}</span><span className="cf-stat-label">Complaints</span></div>
        <div className="cf-stat open"><span className="cf-stat-value">{counts.open}</span><span className="cf-stat-label">Open</span></div>
        <div className="cf-stat closed"><span className="cf-stat-value">{counts.closed}</span><span className="cf-stat-label">Closed</span></div>
      </div>

      <div className="cf-controls">
        <div className="cf-filter-group">
          {['All', 'complaint', 'compliment', 'suggestion', 'enquiry'].map(t => (
            <button key={t} className={`cf-filter-btn ${typeFilter === t ? 'active' : ''}`} onClick={() => setTypeFilter(t)}>
              {t === 'All' ? 'ALL' : TYPE_CONFIG[t]?.label.toUpperCase()}
            </button>
          ))}
        </div>
        <div className="cf-filter-group">
          {['All', 'open', 'in progress', 'closed'].map(s => (
            <button key={s} className={`cf-filter-btn ${statusFilter === s ? 'active' : ''}`} onClick={() => setStatusFilter(s)}>
              {s === 'All' ? 'ALL' : STATUS_CONFIG[s]?.label.toUpperCase()}
            </button>
          ))}
        </div>
        {user.role === 'admin' && <button className="cf-add-btn" onClick={() => { setForm(EMPTY_FORM); setView('add'); }}>+ LOG FEEDBACK</button>}
      </div>

      {feedback.length === 0 ? (
        <div className="cf-empty">
          <div className="cf-empty-icon">💬</div>
          <p>No customer feedback logged yet.</p>
          {user.role === 'admin' && <button className="cf-btn primary" onClick={() => setView('add')}>Log your first feedback</button>}
        </div>
      ) : filtered.length === 0 ? (
        <div className="cf-empty"><p>No feedback matches your filters.</p></div>
      ) : (
        <div className="cf-list">
          {filtered.map(fb => {
            const type = TYPE_CONFIG[fb.type] || TYPE_CONFIG.complaint;
            const status = STATUS_CONFIG[fb.status] || STATUS_CONFIG.open;
            return (
              <div key={fb.id} className="cf-card" onClick={() => { setSelected(fb); setView('detail'); }}>
                <div className="cf-card-left">
                  <div className="cf-card-top">
                    <span className={`cf-type-badge ${type.cls}`}>{type.icon} {type.label}</span>
                    <span className="cf-card-ref">{fb.reference_number}</span>
                    <span className="cf-card-date">{formatDate(fb.received_date)}</span>
                  </div>
                  {fb.customer_name && <div className="cf-card-customer">{fb.customer_name}</div>}
                  <div className="cf-card-desc">{fb.description}</div>
                </div>
                <div className="cf-card-right">
                  <span className={`cf-status-badge ${status.cls}`}>{status.label}</span>
                  {fb.satisfaction_rating && <StarRating rating={fb.satisfaction_rating} />}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
